import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { MailOpen } from "lucide-react";
import coverImage from "@/assets/welcome/cover.jpeg";

interface WelcomeSectionProps {
  onOpen: () => void;
  coverUrl?: string | null;
  ornamentUrl?: string;
}

export function WelcomeSection({
  onOpen,
  coverUrl,
  ornamentUrl,
}: WelcomeSectionProps) {
  const [invitedName, setInvitedName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedName = params.get("x");

    try {
      if (!encryptedName) throw "No name provided";
      const decodedName = atob(encryptedName);
      setInvitedName(decodedName);
    } catch (e) {
      // If decoding fails, redirect to home page
      navigate("/");
    }
  }, [navigate]);

  return (
    <motion.div
      className="fixed inset-0 z-50 bg-[#F7F5F2] flex flex-col items-center justify-center text-center p-6 overflow-hidden"
      initial={{ opacity: 1 }}
      exit={{ y: "-100%", transition: { duration: 0.8, ease: "easeInOut" } }}
    >
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={coverUrl || coverImage}
          alt="Welcome Cover"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/40" />{" "}
        {/* Overlay for text readability */}
      </div>

      <div className="relative z-10 flex flex-col items-center w-full max-w-md text-white">
        <p className="font-serif italic mb-4 tracking-widest uppercase text-xs opacity-90">
          The Wedding of
        </p>
        <h1 className="font-handwriting text-5xl md:text-7xl mb-6 drop-shadow-md">
          Rifa & Nauval
        </h1>

        {/* Decoration */}
        {ornamentUrl && (
          <img
            src={ornamentUrl}
            alt="Decoration"
            className="w-48 h-auto mb-8 opacity-90 brightness-0 invert"
          />
        )}

        <motion.div
          className="w-full bg-white/10 backdrop-blur-md p-8 rounded-xl border border-white/20 shadow-2xl"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <p className="text-white/90 mb-3 text-sm">
            Kepada Yth. Bapak/Ibu/Saudara/i
          </p>

          <h2 className="font-serif text-2xl font-bold text-white mb-6 break-words">
            {invitedName || "Tamu Undangan"}
          </h2>

          <p className="text-xs text-white/80 mb-8 leading-relaxed">
            Tanpa mengurangi rasa hormat, kami bermaksud mengundang Anda untuk
            hadir pada acara pernikahan kami.
          </p>

          <Button
            onClick={onOpen}
            className="bg-white text-stone-900 hover:bg-white/90 gap-2 w-full sm:w-auto rounded-full px-8 py-6 text-sm tracking-wide transition-all duration-300 hover:shadow-md hover:scale-105"
          >
            <MailOpen className="w-4 h-4" />
            Buka Undangan
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
}
